import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import OldCart from "./cart";
import NumberGrid from "./numberGrid";
import SearchNumber from "./search";
import { saveCart, getSavedCart, removeSavedCart } from "../../modules/localStorage";
import { hasLogin, hasPixParam, hasWalletParam, hasWrapper } from "../../utils/getPreTickets";
import { Cart as FloatingCart } from "./layout/cart/Cart";
import { emitIntent } from "../../newDesign/utils/emitIntent";
import { R$ } from "../utils/brl";
import { RifaBuyerRanking } from "./layout/buyerRanking";

import {
  AnalyticsSetGameStep
} from "../../reducers/analytics/analyticsActions";

const MOBILE_BREAKPOINT = 900;
const SECTION_LENGTH = 500;

function SelectNumbersRifa({ rifa, disabledNumbers, finishBet, calculateAvailableQuotas, minimumAvailableQuotas }) {
  const dispatch = useDispatch();

  const [cart, setCart] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(false);

  const isMobile = windowWidth < MOBILE_BREAKPOINT;
  const isDesktop = !isMobile;

  const { numberAmount } = rifa;

  useEffect(() => {
    function handleWindowSizeChange() {
      setWindowWidth(window.innerWidth);
    }

    const savedCart = getSavedCart(rifa, 'rifa-cart');
    if(savedCart){
      setCart(savedCart);
    }

    window.addEventListener("resize", handleWindowSizeChange);

    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const numberArray = useMemo(() => {
    return Array.from(new Array(numberAmount)).map((_, index) => index);
  }, []);

  const enabledNumberArray = useMemo(() => {
    const numberLength = Math.log10(rifa.numberAmount);

    return numberArray.filter(
      (num) => !disabledNumbers[num.toString().padStart(numberLength, 0)]
    );
  }, [numberArray, disabledNumbers]);

  const selections = useMemo(() => {
    return cart.map((bet) => bet.number);
  }, [cart]);

  const numberLength = Math.floor(Math.log10(numberAmount - 1)) + 1;
  const allowMultiSelection = useMemo(() => {
    return rifa && ["sem-validacao"].includes(rifa.tipo);
  }, []);

  /**
   * @param {number} number
   * @returns {string|null}
   */
  function selectNumber(number) {
    dispatch(AnalyticsSetGameStep("Select Number"));
    if (typeof number != "number") number = parseInt(number);

    if (isNaN(number)) return "Digite um número";
    if (number < numberArray[0] || number > numberArray[numberArray.length - 1]) return "Número não encontrado";

    const numberLength = Math.log10(rifa.numberAmount);
    if (disabledNumbers[number.toString().padStart(numberLength, 0)]) {
      return "Número já reservado.";
    }

    if (rifa.tipo === "com-validacao" && selections.includes(number)) {
      return "Número já está no carrinho.";
    }

    if (isNumberSelected(number)) {
      return incrementNumber(number);
    }

    const newcart = [...cart];

    newcart.push({
      number,
      value: rifa.value || 0,
      amount: 1,
    });

    setCart(newcart);
    saveCart(rifa, newcart, 'rifa-cart');

    return "";
  }

  /**
   * @param {number} number
   */
  function deselectNumber(number) {
    const newcart = cart.filter((bet) => bet.number != number);

    setCart(newcart)
    saveCart(rifa, newcart, 'rifa-cart');
  }

  function applySurpresinha(length = 1) {
    dispatch(AnalyticsSetGameStep("Chose Lucky Number"));
    for (let i = 0; i < length; i++) {
      let isNumberAvailable = !(rifa.tipo === "com-validacao");
      do {
        const number = Math.floor(Math.random() * enabledNumberArray.length);
        if (!selections.includes(enabledNumberArray[number])) {
          isNumberAvailable = true;
          selectNumber(enabledNumberArray[number]);
        }
      } while (isNumberAvailable === false);
    }
  }

  function isNumberSelected(number) {
    return !!cart.find((bet) => bet.number == number);
  }
  

  /**
   * @param {number} number
   */
  function incrementNumber(number) {
    const newcart = [...cart];

    const bet = newcart.find((bet) => bet.number === number);
    if (!bet) return;

    bet.amount++;

    setCart(newcart);
    saveCart(rifa, newcart, 'rifa-cart');
  }

  /**
   * @param {number} index
   */
  function decrementNumber(number) {
    const newcart = [...cart];

    const bet = newcart.find((bet) => bet.number === number);
    if (!bet) return;

    bet.amount--;

    setCart(newcart);
    saveCart(rifa, newcart, 'rifa-cart');

    if (bet.amount <= 0) deselectNumber(number);
  }

  /**
   * @param {boolean} bypassConfirmation
   */
  function clearCart(bypassConfirmation = false) {
    dispatch(AnalyticsSetGameStep("Clear Cart"));
    if (
      bypassConfirmation ||
      window.confirm("Tem certeza de quer limpar o carrinho?")
    ) {
      setCart([]);
      removeSavedCart('rifa-cart');
    }
    setLoading(false);
  }

  function sendBet(cart, clearCart, isPreTicket, isPix) {
    setLoading(true);
    saveCart(rifa, cart, 'rifa-cart');
    finishBet(cart, clearCart, isPreTicket, isPix);
  }

  if (!enabledNumberArray.length) {
    return (
      <p className="rifa-title text-center">
        Todos os números já foram reservados
      </p>
    );
  }

  const authenticated = hasLogin();

  const validateWithLogin = () => {
    if (hasLogin()) {
      sendBet({ cart }, clearCart, false, true)
    } else {
      emitIntent("open_login");
    }
  }

  const totalValue = cart.reduce((sum, bet) => sum + bet.value * bet.amount, 0);
  const minimumQuotas = Math.max(1, minimumAvailableQuotas || 1);

  const canFinishWithPix = hasPixParam() && totalValue > 0 && cart.length >= minimumQuotas;
  const canFinishWithWallet = totalValue > 0 && hasWalletParam() && cart.length >= minimumQuotas;

  const useOldCart = !hasWrapper();

  return (
    <React.Fragment>
        { !useOldCart ? (<FloatingCart
          totalAmount={R$(totalValue)}
          bets={cart.map((bet) => ({number: bet.number, amount: bet.amount}))}
          maxNumberSize={rifa.game.tnyTamanhoMax}
          onFinishClick={() => {
            if (canFinishWithPix) {
              validateWithLogin();
            } else if (canFinishWithWallet) {
              sendBet({ cart }, clearCart, false, false)
            }
          }}
          disableFinishButton={!(canFinishWithPix || canFinishWithWallet)}
          onRemoveClick={(number) => rifa.tipo === "sem-validacao" ? decrementNumber(number) : deselectNumber(number)}
          onClearClick={clearCart}
          messages={{
            afterTotals: cart.length < minimumQuotas ? <span style={{color: "darkred", fontSize: "12px"}}>Selecione ao menos {minimumQuotas} cota{minimumQuotas != 1 ? "s" : ""}</span> : null
          }}
          keepOpenWhenEmpty={false}
        />) : null }

      <p className="rifa-title">Cotas Disponíveis</p>
      <div className="quota-container">
        {/* <h4>Selecione sua cota</h4> */}
        {calculateAvailableQuotas &&
          <h4><span>{`${calculateAvailableQuotas}%`}</span>{` de Cotas Disponíveis.`}</h4>
        }
      </div>
      <section className="column-rifa">
        <SearchNumber
          onSearch={selectNumber}
          onSurpresinha={applySurpresinha}
          max={rifa.game.tnyTamanhoMax}
        />
      </section>
      <section className="column-rifa">
        <main className="game">
          <NumberGrid
            array={numberArray}
            selections={selections}
            sectionLength={SECTION_LENGTH}
            onSelect={selectNumber}
            numberLength={numberLength}
            allowMultiSelection={allowMultiSelection}
            onDeselectNumber={deselectNumber}
            disabledNumbers={disabledNumbers}
          />
        </main>

        {isDesktop && useOldCart ? (
          <aside class="cart-wrapper">
            <OldCart
              cart={cart}
              onAdd={incrementNumber}
              onSubtract={decrementNumber}
              onRemove={deselectNumber}
              onConfirm={(isPreTicket, isPix) => sendBet({ cart }, clearCart, isPreTicket, isPix)}
              onClear={clearCart}
              numberLength={numberLength}
              loading={loading}
              minimumAvailableQuotas={minimumAvailableQuotas}
            />
          </aside>
        ) : null}
      </section>
      {isMobile && useOldCart ? (
        <OldCart
          cart={cart}
          numberLength={numberLength}
          onAdd={incrementNumber}
          onSubtract={decrementNumber}
          onRemove={deselectNumber}
          onConfirm={(isPreTicket, isPix) => sendBet({ cart }, clearCart, isPreTicket, isPix)}
          onClear={clearCart}
          loading={loading}
          minimumAvailableQuotas={minimumAvailableQuotas}
        />
      ) : null}
    </React.Fragment>
  );
}

export default SelectNumbersRifa;



// WEBPACK FOOTER //
// ./src/rifa/components/selectNumbers.jsx